import { BrowserRouter as Router, Route, Routes, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import { Suspense, useEffect } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ADMIN_ROLES, MANAGE_ROLES, STAFF_ROLES } from './utils/roles';
import { lazyPage } from './utils/lazyPage';
import PrimaryHeader from './Components/Primaryheader/PrimaryHeader';
import Footer from './Components/Footer/Footer';
import { PageBoundary, PageLoading, PageMessage } from './Components/PageStatus/PageStatus';
import Landing from './Pages/Landing/Landing';
import FoodMenu from './Pages/NewMenu/FoodMenu';
import OrderPage from './Pages/NewMenu/ordering/OrderScreen';
import Catering from './Pages/Catering/Catering';
import Login from './Pages/Auth/Login';
import Signup from './Pages/Auth/Signup';
import ForcePasswordChange from './Pages/Auth/ForcePasswordChange';
import FeedbackPage from './Pages/Feedback/FeedbackPage';
import EventFormPublic from './Pages/Events/EventFormPublic';
import GlobalNav from './Components/SideNav/GlobalNav';
import PageTracker from './hooks/usePageTracking';
import AppUpdate from './Components/AppUpdate/AppUpdate';

// The pages above are in the main bundle: customers open them first. Staff pages load on
// their first visit instead, so customers never download them (staff devices fetch them all
// after sign-in, StaffPreload). The kids and game pages are public but rarely opened.
const MenuManager = lazyPage(() => import(/* webpackChunkName: "page-menu-manager" */ './Pages/MenuManager/MenuManager'));
const CateringPlanner = lazyPage(() => import(/* webpackChunkName: "page-catering-planner" */ './Pages/CateringPlanner/CateringPlanner'));
const Account = lazyPage(() => import(/* webpackChunkName: "page-account" */ './Pages/Auth/Account'));
const AdminUsers = lazyPage(() => import(/* webpackChunkName: "page-admin" */ './Pages/Auth/AdminUsers'));
const ShiftClock = lazyPage(() => import(/* webpackChunkName: "page-clock" */ './Pages/Shifts/ShiftClock'));
const ShiftDashboard = lazyPage(() => import(/* webpackChunkName: "page-shifts" */ './Pages/Shifts/ShiftDashboard'));
const ShiftPlanner = lazyPage(() => import(/* webpackChunkName: "page-shift-planner" */ './Pages/Shifts/ShiftPlanner'));
const TaskDashboard = lazyPage(() => import(/* webpackChunkName: "page-tasks" */ './Pages/Tasks/TaskDashboard'));
const DailyOps = lazyPage(() => import(/* webpackChunkName: "page-daily-ops" */ './Pages/DailyOps/DailyOps'));
const BotConfig = lazyPage(() => import(/* webpackChunkName: "page-bot-config" */ './Pages/BotConfig/BotConfig'));
const TicketDashboard = lazyPage(() => import(/* webpackChunkName: "page-tickets" */ './Pages/Tickets/TicketDashboard'));
const SquareDashboard = lazyPage(() => import(/* webpackChunkName: "page-square-sales" */ './Pages/SquareDashboard/SquareDashboard'));
const StorageCleanup = lazyPage(() => import(/* webpackChunkName: "page-storage-cleanup" */ './Pages/StorageCleanup/StorageCleanup'));
const OrderStatus = lazyPage(() => import(/* webpackChunkName: "page-order-status" */ './Pages/OrderStatus/OrderStatus'));
const ExpenseCalculator = lazyPage(() => import(/* webpackChunkName: "page-expenses" */ './Pages/ExpenseCalculator/ExpenseCalculator'));
const MyDay = lazyPage(() => import(/* webpackChunkName: "page-my-day" */ './Pages/MyDay/MyDay'));
const ActiveSessions = lazyPage(() => import(/* webpackChunkName: "page-sessions" */ './Pages/Sessions/ActiveSessions'));
const MenuPoster = lazyPage(() => import(/* webpackChunkName: "page-menu-poster" */ './Pages/MenuPoster/MenuPoster'));
const EventsDashboard = lazyPage(() => import(/* webpackChunkName: "page-events" */ './Pages/Events/EventsDashboard'));
const EventBuilder = lazyPage(() => import(/* webpackChunkName: "page-event-builder" */ './Pages/Events/EventBuilder'));
const EventResponses = lazyPage(() => import(/* webpackChunkName: "page-event-responses" */ './Pages/Events/EventResponses'));
const DesiBitesDash = lazyPage(() => import(/* webpackChunkName: "page-game" */ './Pages/Game/DesiBitesDash'));
const KidsSection = lazyPage(() => import(/* webpackChunkName: "page-kids" */ './Pages/KidsSection/KidsSection'));

const STAFF_PAGES = [
  MenuManager, CateringPlanner, Account, AdminUsers, ShiftClock, ShiftDashboard, ShiftPlanner,
  TaskDashboard, DailyOps, BotConfig, TicketDashboard, SquareDashboard, StorageCleanup, OrderStatus,
  ExpenseCalculator, MyDay, ActiveSessions, MenuPoster, EventsDashboard, EventBuilder, EventResponses,
];

/**
 * ProtectedRoute — redirects if user doesn't have the required role.
 * roles: array of allowed roles. If empty/omitted, just requires login.
 */
function ProtectedRoute({ element, roles = [] }) {
  const { user, role, loading } = useAuth();
  if (loading) return null; // wait for auth to resolve
  if (!user) return <Navigate to="/login" replace />;
  if (roles.length > 0 && !roles.includes(role)) {
    return (
      <PageMessage
        icon="🔒"
        title="Access Restricted"
        action={<Link className="page-status__link" to="/">← Back to Home</Link>}
      >
        You don't have permission to view this page.<br />
        Please contact an admin if you need access.
      </PageMessage>
    );
  }
  return element;
}

/**
 * On a staff device, fetch every staff page once the app is idle. Pages then open at once,
 * and still open if the connection drops later (the truck tablet).
 */
function StaffPreload() {
  const { user, role } = useAuth();
  const staff = !!user && STAFF_ROLES.includes(role);
  useEffect(() => {
    if (!staff) return undefined;
    const preloadAll = () => {
      Promise.allSettled(STAFF_PAGES.map((Page) => Page.preload())).then((results) => {
        const failed = results.filter((r) => r.status === 'rejected');
        if (failed.length) console.warn(`[App] ${failed.length} pages not preloaded:`, failed[0].reason?.message || failed[0].reason);
      });
    };
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(preloadAll, { timeout: 5000 });
      return () => window.cancelIdleCallback(id);
    }
    const timer = setTimeout(preloadAll, 2000);
    return () => clearTimeout(timer);
  }, [staff]);
  return null;
}

/** The page area: the page, "Loading…" while its code arrives, or a message if it fails. */
function PageArea({ children }) {
  const { pathname } = useLocation();
  return (
    <PageBoundary resetKey={pathname}>
      <Suspense fallback={<PageLoading />}>{children}</Suspense>
    </PageBoundary>
  );
}

/** Holds the whole app behind "choose a new password" after a temporary one. */
function PasswordGate({ children }) {
  const { user, mustChangePassword } = useAuth();
  if (user && mustChangePassword) return <ForcePasswordChange />;
  return children;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function RedirectHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const redirect = params.get('redirect');
    if (redirect && location.pathname === '/') {
      // Decode once — guard against double-encoded values from old 404 bounces
      const target = decodeURIComponent(redirect);
      // Only navigate if it looks like a clean path (no nested ?redirect=)
      if (target.startsWith('/') && !target.includes('redirect=')) {
        navigate(target, { replace: true });
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function App() {
  return (
    <Router basename="/">
      <AuthProvider>
        <ScrollToTop />
        <RedirectHandler />
        <PageTracker />
        <StaffPreload />
        <GlobalNav />
        <AppUpdate />
        <div className="App">
          <PrimaryHeader />
          <PasswordGate>
            <PageArea>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/menu" element={<FoodMenu />} />
                {/* Legacy duplicate menu URLs → canonical lowercase /menu (SEO dedup) */}
                <Route path="/FoodMenu" element={<Navigate to="/menu" replace />} />
                <Route path="/Menu" element={<Navigate to="/menu" replace />} />
                <Route path="/order" element={<OrderPage />} />
                {/* Same ordering screen for the shared tablet at the truck */}
                <Route path="/kiosk" element={<OrderPage kiosk />} />
                <Route path="/menu-manager" element={<ProtectedRoute element={<MenuManager />} roles={MANAGE_ROLES} />} />
                <Route path="/Catering" element={<Catering />} />
                <Route path="/catering-planner" element={<ProtectedRoute element={<CateringPlanner />} roles={MANAGE_ROLES} />} />
                <Route path="/catering-planner/:planId" element={<ProtectedRoute element={<CateringPlanner />} roles={MANAGE_ROLES} />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/account" element={<ProtectedRoute element={<Account />} />} />
                <Route path="/admin" element={<ProtectedRoute element={<AdminUsers />} roles={ADMIN_ROLES} />} />
                <Route path="/clock" element={<ProtectedRoute element={<ShiftClock />} roles={STAFF_ROLES} />} />
                <Route path="/shifts" element={<ProtectedRoute element={<ShiftDashboard />} roles={STAFF_ROLES} />} />
                <Route path="/shift-planner" element={<ProtectedRoute element={<ShiftPlanner />} roles={MANAGE_ROLES} />} />
                <Route path="/tasks" element={<ProtectedRoute element={<TaskDashboard />} roles={STAFF_ROLES} />} />
                <Route path="/daily-ops" element={<ProtectedRoute element={<DailyOps />} roles={STAFF_ROLES} />} />
                <Route path="/bot-config" element={<ProtectedRoute element={<BotConfig />} roles={ADMIN_ROLES} />} />
                <Route path="/tickets" element={<ProtectedRoute element={<TicketDashboard />} roles={STAFF_ROLES} />} />
                <Route path="/square-sales" element={<ProtectedRoute element={<SquareDashboard />} roles={ADMIN_ROLES} />} />
                <Route path="/storage-cleanup" element={<ProtectedRoute element={<StorageCleanup />} roles={ADMIN_ROLES} />} />
                <Route path="/order-status" element={<ProtectedRoute element={<OrderStatus />} />} />
                <Route path="/expense-calculator" element={<ProtectedRoute element={<ExpenseCalculator />} roles={['owner']} />} />
                <Route path="/my-day" element={<ProtectedRoute element={<MyDay />} roles={['owner']} />} />
                <Route path="/sessions" element={<ProtectedRoute element={<ActiveSessions />} roles={['owner']} />} />
                <Route path="/game" element={<DesiBitesDash />} />
                <Route path="/kids" element={<KidsSection />} />
                <Route path="/menu-poster" element={<ProtectedRoute element={<MenuPoster />} roles={ADMIN_ROLES} />} />
                <Route path="/feedback" element={<FeedbackPage />} />
                {/* Event Pre-Orders — admin builder/dashboard + public shareable form */}
                <Route path="/events" element={<ProtectedRoute element={<EventsDashboard />} roles={MANAGE_ROLES} />} />
                <Route path="/events/new" element={<ProtectedRoute element={<EventBuilder />} roles={MANAGE_ROLES} />} />
                <Route path="/events/:id/edit" element={<ProtectedRoute element={<EventBuilder />} roles={MANAGE_ROLES} />} />
                <Route path="/events/:id/responses" element={<ProtectedRoute element={<EventResponses />} roles={MANAGE_ROLES} />} />
                <Route path="/event/:id" element={<EventFormPublic />} />
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </PageArea>
          </PasswordGate>
          <Footer />
        </div>
      </AuthProvider>
    </Router>
  );
}

export default App;
